import { useBasicSettingsData } from "api/basic-settings";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useExports } from "api/exports";
import {
  hasAnyAccessToResource,
  hasViewAccessToResource,
  useSettings,
} from "api/general";
import { useTiers } from "api/tiers";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as Settings01Icon } from "components/Icons/settings-01.svg";
import { LoadingInterceptor } from "components/LoadingInterceptor/LoadingInterceptor";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { TIER_LIST_ID } from "state/tables/types";
import { ExportType } from "types/exports";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { TierType } from "types/tierSets/tierSet";
import { backendErrors } from "utilities/backendErrors";
import { links } from "utilities/links";

function CreateItems(items: any[]): Row[] {
  return items.map((item) => ({
    cells: [
      { content: item.name, property: "name" },
      {
        align: "right",
        content: item.conditionValue,
        emptyString: "0",
        property: "conditionValue",
      },
    ],
    id: item.levelId,
    metadata: { disabled: !item.active },
  }));
}

export function TierList() {
  const { listing, updateListing } = useListing(TIER_LIST_ID);
  const appDispatch = useAppDispatch();
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[TIER_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { updateSettings } = useSettings();
  const settingsData = useBasicSettingsData("tiers");
  const tiersMode = settingsData.formValues?.tiersMode;
  const isTierModeAlreadyChosen = !!tiersMode;

  const { activateTier, deactivateTier, removeTier, useListOfTiers } =
    useTiers();

  const { data, error, isValidating } = useListOfTiers({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
    dynamicSearch,
  });

  const items = data?.items ? CreateItems(data.items) : [];
  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "conditionValue" }),
      property: "conditionValue",
      isSortable: true,
    },
  ]);
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      TIER_LIST_ID,
    );

    setFilters(filters);
  };
  const [selectedModal, setModal] = useState<{
    tier: string;
    mode: number;
  }>();
  enum Modals {
    Activate,
    Deactivate,
    Remove,
    Export,
  }

  const { notify } = useNotifications();
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<any>();

  const hasViewTierPermission = hasViewAccessToResource("LEVEL");
  const { exportList } = useExports();

  const handleExportMemberList = async () => {
    const data = await exportList(ExportType.MEMBER_TIER, {
      memberTier: {
        _orderBy: { createdAt: "desc" },
        _page: listing.page,
        _itemsOnPage: listing.rowsPerPage,
        tierId: selectedModal?.tier,
      },
    });

    if (data) {
      notify(message({ id: "exportSnackbarInfo" }), {
        button: {
          text: message({ id: "viewList" }),
          onClick: ({ closeNotification }) => {
            closeNotification();

            return navigate(links.importsExports, {
              state: { activeTab: "exports" },
            });
          },
        },
      });
      setOpen(false);
    }
  };

  useEffect(() => {
    if (tiersMode && tiersMode === "custom") navigate(links.tierSetList);
  }, [tiersMode, navigate]);

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, TIER_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");
  const hasViewExportPermission = hasViewAccessToResource("EXPORT");

  const updateTierMode = async (tierMode: TierType) => {
    try {
      const response = await updateSettings({ tiersMode: tierMode });

      if (response?.data?.settings) {
        return navigate(links.addTierSet);
      }
    } catch (exception) {
      notify(getErrorMessage(exception));
    }
  };

  const isLoading = !data || !settingsData || !!isValidating;

  return (
    <LoadingInterceptor isLoading={isLoading}>
      <TopHeader title={message({ id: "tiers" })} />
      <Subheader>
        <FiltersV2
          data-test-id="add-filter"
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "description",
              label: message({ id: "description" }),
              type: "text",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "tier-name-filter",
            },
            {
              query: "conditionValue",
              label: message({ id: "conditionValue" }),
              type: "number",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
          donutLabel={message({ id: "tiers" })}
        />
        <SubheaderWrapper>
          {isTierModeAlreadyChosen ? (
            <SubheaderLinkButton
              text={message({ id: "configuration" })}
              link={links.tiersConfiguration}
              icon={<Settings01Icon />}
              variant="outlined"
              data-test-id="configuration"
            />
          ) : null}
          <SubheaderActionButton
            text={message({ id: "addTier" })}
            onClick={() => {
              if (!isTierModeAlreadyChosen) {
                return updateTierMode(TierType.TIER_SET);
              }

              if (tiersMode === TierType.SINGLE_TIER) {
                return navigate(links.addTier);
              }
              return navigate(links.addTierSet);
            }}
            variant="contained"
            disabled={hasViewTierPermission || !hasAnyLanguagePermission}
            data-test-id="tiers-add-tier-add-tier-button"
          />
        </SubheaderWrapper>
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            initialInputValue={dynamicSearch?.value}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: TIER_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={TIER_LIST_ID}
        data-test-id="tiers"
        onRowClick={(id: string) => {
          hasAnyLanguagePermission && navigate(links.editTier(id as string));
        }}
        sort={{
          order: listing.order || "asc",
          property: listing.property || "name",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({ tier: id as string, mode: Modals.Activate });
                setOpen(true);
              },
              disableCondition: () => hasViewTierPermission,
              hideCondition: (item: Row) => !item.metadata?.disabled,
              label: message({ id: "activateTier" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  tier: id as string,
                  mode: Modals.Deactivate,
                });
                setOpen(true);
              },
              disableCondition: () => hasViewTierPermission,
              hideCondition: (item: Row) => item.metadata?.disabled,
              label: message({ id: "deactivateTier" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editTier(id as string)),
              disableCondition: () => !hasAnyLanguagePermission,
              label: message({ id: "editTier" }),
            },
            {
              label: message({ id: "exportMembersList" }),
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({ tier: id as string, mode: Modals.Export });
                setOpen(true);
              },
              hideCondition: () => hasViewExportPermission,
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({ tier: id as string, mode: Modals.Remove });
                setOpen(true);
              },
              disableCondition: () => hasViewTierPermission,
              label: message({ id: "removeTier" }),
            },
          ],
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
      />
      {selectedModal?.mode === Modals.Activate ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await activateTier(selectedModal?.tier);
              if (response) {
                setOpen(false);
                notify(message({ id: "tierActivated" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          close={() => {
            setOpen(false);
            setErrors(null);
          }}
          errorInfo={errors?.[""]}
          header="activateHeader"
          info="activateTierInfo"
          open={open}
        />
      ) : selectedModal?.mode === Modals.Deactivate ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await deactivateTier(selectedModal?.tier);
              if (response) {
                setOpen(false);
                notify(message({ id: "tierDeactivated" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          close={() => {
            setOpen(false);
            setErrors(null);
          }}
          errorInfo={errors?.[""]}
          header="thisTierWillBeDeactivated"
          info="deactivateThisTier"
          open={open}
        />
      ) : selectedModal?.mode === Modals.Remove ? (
        <ConfirmationModal
          action={async () => {
            try {
              const response = await removeTier(selectedModal?.tier);
              if (response) {
                setOpen(false);
                notify(message({ id: "tierRemoved" }));
              }
            } catch (exception) {
              notify(getErrorMessage(exception));
              setErrors(backendErrors(getBackendErrors(exception)));
            }
          }}
          data-test-id="tier-remove"
          close={() => {
            setOpen(false);
            setErrors(null);
          }}
          errorInfo={errors?.[""]}
          header="thisTierWillBeRemoved"
          info="deleteInfo"
          open={open}
        />
      ) : selectedModal?.mode === Modals.Export ? (
        <ConfirmationModal
          action={() => handleExportMemberList()}
          close={() => setOpen(false)}
          header="exportConfirmation"
          info=""
          open={open}
          cancelText={message({ id: "cancel" })}
          submitText="export"
        />
      ) : null}
    </LoadingInterceptor>
  );
}
